import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import SegmentedControl from "../SegmentedControl";
import { SignupStore, Gender, MIN_AGE } from "../../hooks/useSignupStore";

interface Props {
  store: SignupStore;
}

const BirthdateStep: React.FC<Props> = ({ store }) => {
  const { t } = useTranslation();
  const { textNeutralDefault, textNeutralMuted, colorErrorDefault } =
    useColors();

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [age, setAge] = useState<number | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (store.birthdate) {
      setDay(store.birthdate.getDate().toString().padStart(2, "0"));
      setMonth((store.birthdate.getMonth() + 1).toString().padStart(2, "0"));
      setYear(store.birthdate.getFullYear().toString());
    }
  }, []);

  useEffect(() => {
    if (day && month && year && year.length === 4) {
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);

      if (
        dayNum >= 1 &&
        dayNum <= 31 &&
        monthNum >= 1 &&
        monthNum <= 12 &&
        yearNum >= 1920 &&
        yearNum <= new Date().getFullYear()
      ) {
        const date = new Date(yearNum, monthNum - 1, dayNum);

        if (
          date.getDate() === dayNum &&
          date.getMonth() === monthNum - 1 &&
          date.getFullYear() === yearNum
        ) {
          const today = new Date();
          let calculatedAge = today.getFullYear() - date.getFullYear();
          const m = today.getMonth() - date.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
            calculatedAge--;
          }

          setAge(calculatedAge);

          if (date > today) {
            setDateError(
              t("signup.birthdate.future_date") ||
                "Date cannot be in the future",
            );
            store.setBirthdate(null);
          } else if (calculatedAge < MIN_AGE) {
            setDateError(
              `${
                t("signup.birthdate.min_age") || "You must be at least"
              } ${MIN_AGE} ${t("signup.birthdate.years_old") || "years old"}`,
            );
            store.setBirthdate(null);
          } else {
            setDateError(null);
            store.setBirthdate(date);
          }
        } else {
          setDateError(t("signup.birthdate.invalid_date") || "Invalid date");
          store.setBirthdate(null);
        }
      } else {
        store.setBirthdate(null);
        setAge(null);
      }
    } else {
      store.setBirthdate(null);
      setAge(null);
    }
  }, [day, month, year]);

  const genderOptions = [
    { value: Gender.FEMALE, label: t("female") || "Female" },
    { value: Gender.MALE, label: t("male") || "Male" },
    { value: Gender.NON_BINARY, label: t("non_binary") || "Non-binary" },
  ];

  return (
    <StepWrapper
      title={`${t("signup.birthdate.title") || "When were you born"}, ${
        store.name
      }?`}
      subtitle={
        t("signup.birthdate.subtitle") ||
        "This helps us match you with age-appropriate flats"
      }
    >
      <div className="space-y-8">
        {/* Birthdate inputs */}
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2">
            <label
              className="font-medium"
              style={{ color: textNeutralDefault }}
            >
              {t("signup.birthdate.birthdate") || "Birthdate"}
            </label>
            {age !== null && !dateError && (
              <span className="text-sm" style={{ color: textNeutralMuted }}>
                ({age} {t("years") || "years"})
              </span>
            )}
          </div>
          <div className="flex gap-3 justify-center">
            <input
              type="text"
              inputMode="numeric"
              value={day}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                setDay(val);
              }}
              placeholder={t("signup.birthdate.day") || "DD"}
              className="cf-input-boxed text-center text-lg"
              style={{
                width: 80,
                color: textNeutralDefault,
              }}
              autoFocus
            />
            <input
              type="text"
              inputMode="numeric"
              value={month}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                setMonth(val);
              }}
              placeholder={t("signup.birthdate.month") || "MM"}
              className="cf-input-boxed text-center text-lg"
              style={{
                width: 80,
                color: textNeutralDefault,
              }}
            />
            <input
              type="text"
              inputMode="numeric"
              value={year}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                setYear(val);
              }}
              placeholder={t("signup.birthdate.year") || "YYYY"}
              className="cf-input-boxed text-center text-lg"
              style={{
                width: 96,
                color: textNeutralDefault,
              }}
            />
          </div>
          {dateError && (
            <p
              className="text-sm text-center"
              style={{ color: colorErrorDefault }}
            >
              {dateError}
            </p>
          )}
        </div>

        {/* Gender selection - sliding segmented control */}
        <div className="space-y-3">
          <label
            className="font-medium text-center block"
            style={{ color: textNeutralDefault }}
          >
            {t("gender") || "Gender"}
          </label>
          <SegmentedControl
            options={genderOptions}
            value={store.gender}
            onChange={(v) => store.setGender(v)}
          />
        </div>
      </div>
    </StepWrapper>
  );
};

export default BirthdateStep;
